// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LazyImage, mapStateToProps, Props } from "@awc/MobiusOverrides/LazyImage";
import { getCloudinaryUrl, TransformationsOptions } from "@awc/XDL/Utilities";
import { injectCss } from "@insite/mobius/utilities";
import React from "react";
import { connect } from "react-redux";
import styled, { ThemeContext, withTheme } from "styled-components";

const LazyPictureStyle = styled.picture<any>`
    max-height: 100%;
    max-width: 100%;
    img {
        max-width: 100%;
        max-height: 100%;
    }

    ${injectCss}
`;

/**
 * Xng Picture Component
 */
export class LazyPicture extends LazyImage {
    static contextType = ThemeContext;
    declare context: React.ContextType<typeof ThemeContext>;

    componentDidUpdate(prevProps: Props) {
        if (
            this.props.largeImageSrc !== prevProps.largeImageSrc ||
            this.props.mediumImageSrc !== prevProps.mediumImageSrc ||
            this.props.smallImageSrc !== prevProps.smallImageSrc
        ) {
            this.reloadImage();
        }
    }

    hasInvalidProps() {
        const { largeImageSrc, mediumImageSrc, smallImageSrc } = this.props as Props;
        return !largeImageSrc && !mediumImageSrc && !smallImageSrc;
    }

    getImgUrl = (src?: string, transformations?: TransformationsOptions) => {
        const pictureSrc = this.props.disableLazyLoad
            ? this.props.largeImageSrc || this.props.mediumImageSrc || this.props.smallImageSrc
            : "";
        const imgSrc =
            src ||
            this.props.src ||
            pictureSrc ||
            (!this.props.disableFallbackImage ? this.props.notFoundImagePath : "") ||
            "";
        if (!imgSrc) {
            return imgSrc;
        }

        return getCloudinaryUrl(imgSrc, this.props.cloudinaryBaseUrl, transformations) || imgSrc;
    };

    renderImage(src?: string) {
        const { pictureProps, largeImageSrc, mediumImageSrc, smallImageSrc } = this.props as Props;
        const smallScreenMaxWidth = this.context.breakpoints.values[2] - 1;
        return (
            <LazyPictureStyle {...pictureProps}>
                <source
                    media={`(min-width: ${this.context.breakpoints.values[3]}px)`}
                    srcSet={this.getImgUrl(largeImageSrc)}
                />
                <source
                    media={`(min-width: ${this.context.breakpoints.values[2]}px)`}
                    srcSet={this.getImgUrl(mediumImageSrc)}
                />
                <source media={`(max-width: ${smallScreenMaxWidth}px)`} srcSet={this.getImgUrl(smallImageSrc)} />
                {super.renderImage(smallImageSrc, true)}
            </LazyPictureStyle>
        );
    }
}

/** @component */
export default withTheme(connect(mapStateToProps)(LazyPicture));
